import { Grid, Typography, IconButton } from "@mui/material";
import BasicTextField from "../components/forms/formElements/BasicTextField";
import { useEffect, useState } from "react";
import SplitLayout from "../components/forms/formLayouts/SplitLayout";
import { useTheme } from "@mui/material/styles";
import { Link, useNavigate } from "react-router-dom";
import useButtonStyles from "../styles/useButtonStyles";
import axios from "axios";
import { APIConstants } from "../constants/ApiConstants";
import BasicModal from "../components/ui/BasicModal";
import Loader from "../components/ui/Loader";
import DateField from "../components/forms/formElements/DateField";
import CloseIcon from "@mui/icons-material/Close";
import { usePermissionGetter } from "../Hooks/Permissions/userPermissionGetter";
import { CommonButton } from "../Shared/Components/Buttons/CommonButton";
import { errorMessageConstants } from "../constants/AppConstants";

export interface AddPhysicalVoucherFormType {
  voucherNo: string;
  type: string;
  amount: number;
  remarks: string;
  issueDate: string;
  soldTo: string;
  expiryDate: string;
}
interface addPhysicalVoucherProps {
  showPhysicalVoucher?: Function;
  onRefresh?: () => void;
}

const AddPhysicalVoucher = (props: addPhysicalVoucherProps) => {
  const { getUserPermission: getAddPhysicalVoucherPermission } =
    usePermissionGetter("physical_voucher", "physical_voucher");
  const theme = useTheme();
  const { showPhysicalVoucher, onRefresh } = props;
  const initialFormValues: AddPhysicalVoucherFormType = {
    voucherNo: "",
    type: "",
    amount: 0,
    remarks: "",
    issueDate: "",
    soldTo: "",
    expiryDate: "",
  };
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [clearForm, setClearForm] = useState(false);
  const [voucherFormState, setVoucherFormState] =
    useState<AddPhysicalVoucherFormType>({ ...initialFormValues });
  const [showModal, setShowModal] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [axiosErrorMessage, setAxiosErrorMessage] = useState<string>(
    "Something went wrong"
  );
  const navigate = useNavigate();
  function getMinDate() {
    const issueDate = new Date(voucherFormState.issueDate);
    const currentDate = new Date();
    if (issueDate > currentDate) {
      return issueDate.toISOString().split("T")[0];
    }
    return currentDate.toISOString().split("T")[0];
  }

  const getExpiryDateWithUtcTime = (expiryDate: string | Date) => {
    const date = new Date(expiryDate);
    date.setUTCHours(15, 59, 59, 0);
    return date.toISOString();
  };

  const addVoucher = async () => {
    setLoading(true);

    try {
      const data = {
        voucherNo: voucherFormState.voucherNo.trim(),
        type: voucherFormState.type.trim(),
        amount: +voucherFormState.amount,
        remarks: voucherFormState.remarks.trim(),
        issueDate: voucherFormState.issueDate,
        soldTo: voucherFormState.soldTo.trim(),
        expiryDate: getExpiryDateWithUtcTime(voucherFormState.expiryDate),
      };
      const response = await axios.post(
        process.env.REACT_APP_API_URL + APIConstants.addPhysicalVoucher,
        data
      );
      if (response.data.StatusCode === 200) {
        onRefresh && onRefresh();
      }
      setAxiosErrorMessage("Something went wrong");
      setUploadError(false);
    } catch (error: any) {
      console.error("error", error);
      setAxiosErrorMessage(
        error?.response?.data?.Message ?? "Something went wrong"
      );
      setUploadError(true);
    } finally {
      setLoading(false);
      setShowModal(true);
      setClearForm(true);
      setVoucherFormState({ ...initialFormValues });
      setTimeout(() => {
        navigate("/uploadVoucher");
      }, 5000);
      setTimeout(() => {
        setClearForm(false);
      }, 2000);
    }
  };

  const isPastDate = (date: string): boolean => {
    const currentDate = new Date();
    const selectedDate = new Date(date);
    return selectedDate < currentDate;
  };
  const isExpiryValidDate = (date: string): boolean => {
    const issueDate = new Date(voucherFormState.issueDate);
    const ExpiryDate = new Date(date);
    const isValid = date ? ExpiryDate >= issueDate : true;
    return isValid;
  };

  const validateForm = () => {
    const isNumber = (value: any): boolean => {
      return (
        /^\d*\.?\d+$/.test(value) && Number(value) > 0 && Number(value) <= 10000
      );
    };
    const isVoucherNoValid = voucherFormState.voucherNo.trim() !== "";
    const isTypeValid = voucherFormState.type.trim() !== "";
    const isAmountValid =
      isNumber(voucherFormState.amount) && voucherFormState.amount > 0;
    const isIssueDateValid =
      voucherFormState.issueDate !== "" &&
      isPastDate(voucherFormState.issueDate);
    const isSoldToValid = voucherFormState.soldTo.trim() !== "";
    const isExpiryDateValid =
      voucherFormState.expiryDate !== "" &&
      isExpiryValidDate(voucherFormState.expiryDate);
    return (
      isVoucherNoValid &&
      isTypeValid &&
      isAmountValid &&
      isIssueDateValid &&
      isSoldToValid &&
      isExpiryDateValid
    );
  };
  useEffect(() => {
    setDisableSubmit(!validateForm());
  }, [voucherFormState]);
  const onModalClose = () => {
    if (onRefresh) {
      onRefresh();
    }
    if (showPhysicalVoucher) showPhysicalVoucher(false);
  };
  const submitHandler = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    if (!disableSubmit) {
      await addVoucher();
      setVoucherFormState(initialFormValues);
    }
  };
  return (
    <Grid
      borderRadius={"16px"}
      p={3}
      pb={0}
      sx={{ background: theme.color?.background }}
    >
      <Grid
        item
        display={"flex"}
        alignItems={"center"}
        pb={3}
        pt={3}
        justifyContent={"space-between"}
      >
        <Grid display={"flex"}>
          <Typography
            variant="h6"
            sx={{
              color: theme?.color?.primary,
              fontFamily: theme.font?.fontFamily.primary,
              fontWeight: "700",
            }}
            px={2}
          >
            Add Physical Voucher
          </Typography>
        </Grid>
        <Grid display={"flex"}>
          <IconButton
            aria-label="delete"
            onClick={
              showPhysicalVoucher ? () => showPhysicalVoucher(false) : () => {}
            }
          >
            <CloseIcon />
          </IconButton>
        </Grid>
      </Grid>
      <Grid
        display={"flex"}
        direction={"column"}
        justifyContent={"space-between"}
        alignItems={"space-between"}
      >
        <Grid display={"flex"} width={"100%"} py={1}>
          <SplitLayout columns={[6, 6]}>
            <BasicTextField
              label={"Voucher No *"}
              setState={setVoucherFormState}
              stateKey="voucherNo"
              clear={clearForm}
              required={true}
              placeholder={""}
            />
            <Grid>
              <BasicTextField
                label="Type *"
                setState={setVoucherFormState}
                placeholder=""
                stateKey="type"
                variant="outlined"
                align="left"
                required={true}
                clear={clearForm}
              />
            </Grid>
          </SplitLayout>
        </Grid>
        <Grid display={"flex"} width={"100%"} py={1}>
          <SplitLayout columns={[6, 6]}>
            <BasicTextField
              label="Amount *"
              setState={setVoucherFormState}
              placeholder="$"
              stateKey="amount"
              variant="outlined"
              align="left"
              type="number"
              required={true}
              clear={clearForm}
              errorMessage={
                voucherFormState.amount > 10000
                  ? errorMessageConstants.pvAmountErrorMessage
                  : ""
              }
            />
            <BasicTextField
              setState={setVoucherFormState}
              stateKey="soldTo"
              label="Sold to *"
              variant="outlined"
              align="left"
              required={true}
              clear={clearForm}
              defaultValue={voucherFormState.soldTo}
              placeholder={""}
            />
          </SplitLayout>
        </Grid>

        <Grid
          display={"flex"}
          width={"100%"}
          py={1}
          pb={4}
          sx={{ borderBottom: theme.border?.smallGrey }}
        >
          <SplitLayout columns={[6, 6]}>
            <DateField
              setState={setVoucherFormState}
              stateKey="issueDate"
              label="Issue Date *"
              disableSubmit={setDisableSubmit}
              clear={clearForm}
              required
              defaultValue={""}
              minDate={getMinDate()}
            />
            <DateField
              futureDate={true}
              setState={setVoucherFormState}
              stateKey="expiryDate"
              label="Expiry Date *"
              disableSubmit={setDisableSubmit}
              clear={clearForm}
              required
              defaultValue={""}
              errorMessage={
                !isExpiryValidDate(voucherFormState.expiryDate)
                  ? errorMessageConstants.pvExpiryErrorMessage
                  : ""
              }
            />
          </SplitLayout>
        </Grid>

        <Grid display={"flex"} width={"100%"} paddingBottom={6} py={3.5}>
          <SplitLayout columns={[12]}>
            <BasicTextField
              label="Remarks"
              setState={setVoucherFormState}
              placeholder=""
              stateKey="remarks"
              variant="outlined"
              align="left"
              multiline
              rows={4}
              defaultValue={voucherFormState.remarks}
              clear={clearForm}
            />
          </SplitLayout>
          {voucherFormState.remarks.length > 15 && (
            <Typography
              sx={{
                fontFamily: theme.font?.fontFamily.primary,
                color: theme.color?.error,
                ml: 1,
                fontSize: theme.font?.fontSize.tableBody,
              }}
            >
              {errorMessageConstants.pvRemarkMessage}
            </Typography>
          )}
        </Grid>
      </Grid>
      <Grid
        item
        container
        width={"100%"}
        display={"flex"}
        justifyContent={"center"}
        direction={"row"}
        pt={2}
        mt={2}
        sx={{ borderTop: theme.border?.smallGrey }}
      >
        <CommonButton
          buttonTitle="Cancel"
          variant="outlined"
          color="secondary"
          onClick={() => showPhysicalVoucher && showPhysicalVoucher(false)}
          sx={{ width: "40%", mr: 2 }}
        />
        {getAddPhysicalVoucherPermission("create") && (
          <CommonButton
            buttonTitle="save"
            variant="contained"
            color="secondary"
            onClick={submitHandler}
            type="submit"
            disabled={disableSubmit}
            sx={{ width: "40%" }}
          />
        )}
      </Grid>
      {showModal &&
        (uploadError ? (
          <BasicModal
            title={axiosErrorMessage}
            success={false}
            setState={setShowModal}
            redirection="/uploadVoucher"
          />
        ) : (
          <BasicModal
            title={"Voucher Added Successfully!"}
            success={true}
            setState={setShowModal}
            redirection="/uploadVoucher"
            onCloseModule={onModalClose}
          />
        ))}
      {loading && <BasicModal content={<Loader />} />}
    </Grid>
  );
};

export default AddPhysicalVoucher;
